import { Component, Inject, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators, } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetModule, MatBottomSheetRef, } from '@angular/material/bottom-sheet';
import { ApplicationService } from '@lmc-app/core/services/application.service';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { provideNgxMask } from 'ngx-mask';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideDayjsDateAdapter } from '@lmc-app/shared/adapters/dayjs-date-adapter';
import { DateTimePickerComponent } from '@lmc-app/shared/components/date-time-picker/date-time-picker.component';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { User } from '@lmc-app/shared/interface/user.interface';
import { ApplicationCall } from '@lmc-app/shared/interface/application-call.interface';
import { Applicant } from '@lmc-app/shared/enum/applicant.enum';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatSelectSearchUtil } from '@lmc-app/utils/mat-select-search.util';
import { CommonDialog } from '@lmc-app/shared/components/dialog/common-dialog';
import { MatDialog } from '@angular/material/dialog';
import { ApplicationCallRequest } from '@lmc-app/shared/interface/request/application-call.request';
import dayjs from 'dayjs';

@Component({
	selector: 'app-schedule-call-form',
	templateUrl: 'schedule-call-form.component.html',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		DateTimePickerComponent,
		MatDatepickerModule,
		MatSelectModule,
		MatOptionModule,
		MatInputModule,
		FormsModule,
		MatFormFieldModule,
		MatBottomSheetModule,
		MatButtonModule,
		MatIconModule,
		NgIf,
		AsyncPipe,
		NgForOf,
		NgxMatSelectSearchModule,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [
		provideNgxMask(),
		provideDayjsDateAdapter(),
	],
})
export class ScheduleCallFormComponent implements OnInit, OnDestroy {
	application: any = null;
	applicationCall: ApplicationCall = null;
	scheduleCallFormGroup: FormGroup;
	bottomSheetRef =
		inject<MatBottomSheetRef<ScheduleCallFormComponent>>(MatBottomSheetRef);
	usersFilter: FormControl<string> = new FormControl<string>('', {
		nonNullable: true,
	});
	public filteredUsers: ReplaySubject<User[]> = new ReplaySubject<User[]>(1);
	users: User[] = [];
	private readonly destroy$ = new Subject<void>();
	private currentUser: User = null;
	private readonly _snackBar = inject(MatSnackBar);
	private readonly _dialog = inject(MatDialog);

	constructor(
		private readonly applicationService: ApplicationService,
		private readonly formBuilder: FormBuilder,
		@Inject(MAT_BOTTOM_SHEET_DATA) public data: any
	) {
		if (this.data) {
			console.log('testtest', this.data.application);
			this.applicationCall = this.data.applicationCall;
			this.application = this.data.application;
			this.currentUser = this.data.currentUser;

			// Determine the applicant name based on the applicant type
			const applicantType =
				this.applicationCall?.applicant ?? Applicant.MAIN;
			const applicantName =
				applicantType === Applicant.CO
					? `${this.application?.Co_Applicant_Name} ${this.application?.Guarantor_Name}`
					: this.application?.Contact_Name.Full_Name;

			this.scheduleCallFormGroup = this.formBuilder.group({
				applicant: [applicantType, Validators.required],
				dateTime: [
					this.applicationCall?.dateTime ?? dayjs().toDate(),
					Validators.required,
				],
				subject: [
					this.applicationCall?.subject ??
						`Call scheduled with ${applicantName}`,
					Validators.required,
				],
				reminder: [
					this.applicationCall?.reminder.toString() ?? null,
					Validators.required,
				],
				purpose: [this.applicationCall?.purpose ?? null, Validators.required],
				status: [
					this.applicationCall?.status ?? 'SCHEDULED',
					Validators.required,
				],
				ownedBy: [
					this.applicationCall
						? this.applicationCall?.ownedByEmployee.userId
						: this.currentUser?.employeeID,
					Validators.required,
				],
				userId: [this.currentUser?.employeeID, Validators.required],
			});
		}
	}

	onSubmit() {
		const request: ApplicationCallRequest = {
			...this.applicationCall,
			...this.scheduleCallFormGroup.value,
		};

		if (this.applicationCall) {
			this.applicationService
				.updateApplicationCall(
					this.application.id,
					this.applicationCall.uuid,
					request
				)
				.subscribe({
					next: () => {
						this.bottomSheetRef.dismiss(true);
					},
					error: (error) => {
						console.error(error);
						this._snackBar.open(
							error,
							SnackbarConstants.ACTION_DISMISS,
							SnackbarConstants.DURATION_DISMISS_SHORT
						);
					},
				});
		} else {
			this.applicationService
				.createApplicationCall(this.application.id, request)
				.subscribe({
					next: () => {
						this.bottomSheetRef.dismiss(true);
					},
					error: (response) => {
						console.error(response);
						this._snackBar.open(
							response.error.message,
							SnackbarConstants.ACTION_DISMISS,
							SnackbarConstants.DURATION_DISMISS_SHORT
						);
					},
				});
		}
	}

	onDelete() {
		this._dialog
			.open(CommonDialog, {
				data: { title: 'Are you sure?', content: 'Confirm delete this call?' },
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					this.applicationService
						.deleteApplicationCall(
							this.application.id,
							this.applicationCall.uuid,
							this.currentUser.employeeID
						)
						.subscribe({
							next: () => {
								this.bottomSheetRef.dismiss(true);
							},
							error: (response) => {
								console.error(response);
								this._snackBar.open(
									response.error.message,
									SnackbarConstants.ACTION_DISMISS,
									SnackbarConstants.DURATION_DISMISS_SHORT
								);
							},
						});
				}
			});
	}

	ngOnInit() {
		this.usersFilter.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.users,
					searchValue,
					this.filteredUsers
				);
			});

		// Update subject when applicant changes
		this.scheduleCallFormGroup
			.get('applicant')
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe((applicantType) => {
				// Only update if this is not an existing call (to preserve custom subjects)
				if (!this.applicationCall) {
					const applicantName =
						applicantType === Applicant.CO
							? `${this.application?.Co_Applicant_Name} ${this.application?.Guarantor_Name}`
							: this.application?.Contact_Name.Full_Name;
					this.scheduleCallFormGroup
						.get('subject')
						?.patchValue(`Call scheduled with ${applicantName}`, {
							emitEvent: false,
						});
				}
			});

		this.getUsers();
	}

	getUsers() {
		this.applicationService.getUsers().subscribe({
			next: (res) => {
				this.users = res;
				this.filteredUsers.next(this.users.slice());
			},
			error: (response) => {
				console.error(response);
				this._snackBar.open(
					response.error.message,
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
			},
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
